import type {
  WorkspaceDetail,
  WorkspaceEdge,
  WorkspaceNode,
  WorkspaceNodeType,
} from "../../../types/workspace";
import { WORKSPACE_NODE_TYPE_LABELS } from "./workspaceGraphUtils";
import {
  MIN_COMBINED_POLICY_PARENTS,
  WORKSPACE_MULTI_PARENT_TYPES,
  parentIdsFor,
} from "./workspaceReparentUtils";
import { removeWorkspaceNodeFromDetail } from "./workspaceOptimisticReport";

/** Inverse of BE ALLOWED_CHILDREN — used only for FE removal preview notes. */
const ALLOWED_CHILDREN: Record<WorkspaceNodeType, WorkspaceNodeType[]> = {
  account: ["strategy", "report", "simulation"],
  strategy: ["report"],
  report: ["simulation"],
  simulation: ["policy", "simulation"],
  policy: ["combined_policy", "policy", "simulation"],
  combined_policy: ["combined_simulation", "combined_policy"],
  combined_simulation: ["combined_simulation"],
};

/**
 * Children that stay on the canvas when their parent is removed, even when no
 * legal promotion parent exists. Matches BE / refresh: deleting a simulation
 * must not flash-remove its saved policy lineage (policies + combined stack).
 */
function shouldKeepChildWithoutPromotion(
  parentType: WorkspaceNodeType,
  childType: WorkspaceNodeType
): boolean {
  if (parentType === "simulation" && childType === "policy") return true;
  if (parentType === "combined_simulation" && childType === "policy") return true;
  return false;
}

/** Depth-first collect of every node id under `rootId` (not including root). */
export function collectWorkspaceDescendantNodeIds(
  rootId: string,
  edges: WorkspaceEdge[]
): string[] {
  const childrenByParent = new Map<string, string[]>();
  for (const edge of edges) {
    const list = childrenByParent.get(edge.sourceNodeId) ?? [];
    list.push(edge.targetNodeId);
    childrenByParent.set(edge.sourceNodeId, list);
  }

  const found: string[] = [];
  const seen = new Set<string>();
  const stack = [...(childrenByParent.get(rootId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id) || id === rootId) continue;
    seen.add(id);
    found.push(id);
    for (const childId of childrenByParent.get(id) ?? []) {
      stack.push(childId);
    }
  }
  return found;
}

type WorkspaceBreakLinkPlan = {
  nodesToRemove: WorkspaceNode[];
  /** Parent→child promotions to apply after stripping removed nodes/edges. */
  promoteEdges: Array<{ sourceNodeId: string; targetNodeId: string }>;
};

/**
 * Mirrors BE `plan_break_link_promotions` for UI preview + optimistic updates.
 * Multi-parent children (combined_policy) stay on the canvas when ≥2 parents remain.
 */
function planWorkspaceBreakLink(
  rootId: string,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): WorkspaceBreakLinkPlan {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  if (!nodeById.has(rootId)) return { nodesToRemove: [], promoteEdges: [] };

  const parentByChild = new Map<string, string>();
  const childrenByParent = new Map<string, string[]>();
  const parentsByChild = new Map<string, string[]>();
  for (const edge of edges) {
    parentByChild.set(edge.targetNodeId, edge.sourceNodeId);
    const kids = childrenByParent.get(edge.sourceNodeId) ?? [];
    kids.push(edge.targetNodeId);
    childrenByParent.set(edge.sourceNodeId, kids);
    const parents = parentsByChild.get(edge.targetNodeId) ?? [];
    parents.push(edge.sourceNodeId);
    parentsByChild.set(edge.targetNodeId, parents);
  }

  const findValidParent = (startParentId: string | undefined, childType: WorkspaceNodeType): string | null => {
    let current = startParentId;
    const seen = new Set<string>();
    while (current && !seen.has(current)) {
      seen.add(current);
      const parent = nodeById.get(current);
      if (parent && (ALLOWED_CHILDREN[parent.type] ?? []).includes(childType)) {
        return current;
      }
      current = parentByChild.get(current);
    }
    return null;
  };

  const nodesToRemove: string[] = [];
  const promoteEdges: Array<{ sourceNodeId: string; targetNodeId: string }> = [];
  const promoteSeen = new Set<string>();

  const addPromote = (sourceNodeId: string, targetNodeId: string) => {
    const key = `${sourceNodeId}->${targetNodeId}`;
    if (promoteSeen.has(key)) return;
    promoteSeen.add(key);
    promoteEdges.push({ sourceNodeId, targetNodeId });
  };

  const planRemoval = (nodeId: string) => {
    if (nodesToRemove.includes(nodeId)) return;
    const fallbackParent = parentByChild.get(nodeId);
    const removingNode = nodeById.get(nodeId);
    for (const childId of [...(childrenByParent.get(nodeId) ?? [])]) {
      const child = nodeById.get(childId);
      if (!child) continue;
      if (WORKSPACE_MULTI_PARENT_TYPES.has(child.type)) {
        const remaining = (parentsByChild.get(childId) ?? []).filter((p) => p !== nodeId);
        parentsByChild.set(childId, remaining);
        childrenByParent.set(
          nodeId,
          (childrenByParent.get(nodeId) ?? []).filter((c) => c !== childId)
        );
        if (remaining.length >= MIN_COMBINED_POLICY_PARENTS) {
          parentByChild.set(childId, remaining[0]!);
          continue;
        }
        // Derived Edit & run: single-parent combined_policy under another CP is kept.
        if (
          child.type === "combined_policy" &&
          remaining.length === 1 &&
          nodeById.get(remaining[0]!)?.type === "combined_policy"
        ) {
          parentByChild.set(childId, remaining[0]!);
          continue;
        }
        planRemoval(childId);
        continue;
      }
      // Simulation delete: keep policy children so report→…→policy→combined
      // lineage does not vanish until refresh (BE keeps those nodes).
      if (
        removingNode &&
        shouldKeepChildWithoutPromotion(removingNode.type, child.type)
      ) {
        childrenByParent.set(
          nodeId,
          (childrenByParent.get(nodeId) ?? []).filter((c) => c !== childId)
        );
        continue;
      }
      const newParent = findValidParent(fallbackParent, child.type);
      if (newParent && newParent !== nodeId) {
        parentByChild.set(childId, newParent);
        const nextKids = childrenByParent.get(newParent) ?? [];
        nextKids.push(childId);
        childrenByParent.set(newParent, nextKids);
        childrenByParent.set(
          nodeId,
          (childrenByParent.get(nodeId) ?? []).filter((c) => c !== childId)
        );
        addPromote(newParent, childId);
      } else {
        planRemoval(childId);
      }
    }
    nodesToRemove.push(nodeId);
  };

  planRemoval(rootId);
  return {
    nodesToRemove: nodesToRemove
      .map((id) => nodeById.get(id))
      .filter((node): node is WorkspaceNode => Boolean(node)),
    promoteEdges,
  };
}

/**
 * Mirrors BE `plan_break_link_promotions` for UI preview + optimistic updates.
 * Multi-parent children (combined_policy) stay on the canvas when ≥2 parents remain.
 */
export function planWorkspaceNodesRemovedByBreakLink(
  rootId: string,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): WorkspaceNode[] {
  return planWorkspaceBreakLink(rootId, nodes, edges).nodesToRemove;
}

/**
 * Optimistic canvas update after break-link / entity delete: drop removed nodes
 * and rewire promoted children so lineage does not flash-break before refresh.
 */
export function applyWorkspaceBreakLinkToDetail(
  detail: WorkspaceDetail,
  rootId: string
): WorkspaceDetail {
  const plan = planWorkspaceBreakLink(rootId, detail.nodes, detail.edges);
  const removeIds = plan.nodesToRemove.map((node) => node.id);
  const stripped = removeWorkspaceNodeFromDetail(detail, removeIds);
  if (plan.promoteEdges.length === 0) return stripped;

  const survivingIds = new Set(stripped.nodes.map((node) => node.id));
  const existing = new Set(
    stripped.edges.map((edge) => `${edge.sourceNodeId}->${edge.targetNodeId}`)
  );
  const nextEdges = [...stripped.edges];
  for (const promote of plan.promoteEdges) {
    if (!survivingIds.has(promote.sourceNodeId) || !survivingIds.has(promote.targetNodeId)) {
      continue;
    }
    const key = `${promote.sourceNodeId}->${promote.targetNodeId}`;
    if (existing.has(key)) continue;
    existing.add(key);
    nextEdges.push({
      id: `promote-${key}`,
      sourceNodeId: promote.sourceNodeId,
      targetNodeId: promote.targetNodeId,
    });
  }
  return { ...stripped, edges: nextEdges };
}

/**
 * Root + nodes that leave the canvas when breaking `rootId`, leaves-first.
 * Prefer {@link planWorkspaceNodesRemovedByBreakLink} for multi-parent-aware lists.
 */
export function orderWorkspaceNodesForCascadeRemove(
  rootId: string,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): WorkspaceNode[] {
  const planned = planWorkspaceNodesRemovedByBreakLink(rootId, nodes, edges);
  const removeIds = new Set(planned.map((node) => node.id));
  const nodeById = new Map(planned.map((node) => [node.id, node]));

  const childrenByParent = new Map<string, string[]>();
  for (const edge of edges) {
    if (!removeIds.has(edge.sourceNodeId) || !removeIds.has(edge.targetNodeId)) continue;
    const list = childrenByParent.get(edge.sourceNodeId) ?? [];
    list.push(edge.targetNodeId);
    childrenByParent.set(edge.sourceNodeId, list);
  }

  const ordered: WorkspaceNode[] = [];
  const visiting = new Set<string>();
  const visited = new Set<string>();

  const visit = (id: string) => {
    if (visited.has(id) || visiting.has(id)) return;
    visiting.add(id);
    for (const childId of childrenByParent.get(id) ?? []) {
      visit(childId);
    }
    visiting.delete(id);
    visited.add(id);
    const node = nodeById.get(id);
    if (node) ordered.push(node);
  };

  visit(rootId);
  // Any planned removals not reached via the subgraph (shouldn't happen) append last.
  for (const node of planned) {
    if (!visited.has(node.id)) ordered.push(node);
  }
  return ordered;
}

export function describeWorkspaceDownstreamRemoval(
  root: WorkspaceNode,
  descendants: WorkspaceNode[],
  options?: {
    edges?: WorkspaceEdge[];
    nodes?: WorkspaceNode[];
  }
): string | null {
  if (descendants.length === 0) {
    // Removing a policy parent of a shared combined_policy: note that only the lane drops.
    if (root.type === "policy" && options?.edges && options.nodes) {
      const keptCombined = options.nodes.filter((node) => {
        if (node.type !== "combined_policy") return false;
        const parents = parentIdsFor(options.edges!, node.id);
        if (!parents.includes(root.id)) return false;
        const remaining = parents.filter((id) => id !== root.id).length;
        return remaining >= MIN_COMBINED_POLICY_PARENTS;
      });
      if (keptCombined.length > 0) {
        return `This policy’s link into ${keptCombined.length === 1 ? "the combined policy" : "combined policies"} will be removed, but ${keptCombined.length === 1 ? "it stays" : "they stay"} on the canvas because other source policies remain.`;
      }
    }
    return null;
  }
  const counts = new Map<WorkspaceNodeType, number>();
  for (const node of descendants) {
    counts.set(node.type, (counts.get(node.type) ?? 0) + 1);
  }
  const parts: string[] = [];
  (
    ["strategy", "report", "simulation", "policy", "combined_policy", "combined_simulation"] as WorkspaceNodeType[]
  ).forEach((type) => {
    const count = counts.get(type);
    if (!count) return;
    const label = WORKSPACE_NODE_TYPE_LABELS[type].toLowerCase();
    parts.push(`${count} ${label}${count === 1 ? "" : "s"}`);
  });
  if (parts.length === 0) return null;
  const rootLabel = WORKSPACE_NODE_TYPE_LABELS[root.type].toLowerCase();
  return `Connected ${parts.join(", ")} under this ${rootLabel} will leave the canvas too.`;
}
